import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import axios from 'axios';
import { useIntl } from 'react-intl';
import { apiAuthSignup } from 'api/auth';
import { extractSignupSession, parseSignupCaptchaRequirement } from 'api/responseTypes';
import type { AuthSignupRequest } from 'api/requestTypes';
import {
  readSignupCaptchaPersisted,
  writeSignupCaptchaPersisted,
} from 'utils/signupCaptchaStorage';
import { getSignupHcaptchaSiteKey } from 'utils/signupEnv';
import { DIALING_CODE_IRAQ, INVALID_PHONE_NUMBER_REGEX } from 'utils/constants';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useEffect } from 'react';

export interface Credential {
  phone: string;
  session: string;
}

const phoneSchema = yup
  .object({
    phone: yup
      .string()
      .matches(INVALID_PHONE_NUMBER_REGEX, 'loginModal.invalid')
      .required('loginModal.invalid'),
  })
  .required();

type PhoneFormValues = yup.InferType<typeof phoneSchema>;

export const usePhoneAuthStep = (onCredential: (c: Credential) => void) => {
  const dialingCode = localStorage.getItem('dialingCode') || DIALING_CODE_IRAQ;
  const { formatMessage } = useIntl();
  const hcaptchaRef = useRef<HCaptcha | null>(null);
  const captchaTokenRef = useRef<string | null>(null);
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(false);
  const hcaptchaSiteKey = getSignupHcaptchaSiteKey();

  useEffect(() => {
    const stored = readSignupCaptchaPersisted();
    if (stored?.requiresCaptcha === true) {
      setIsCaptchaRequired(true);
      captchaTokenRef.current = null;
      hcaptchaRef.current?.resetCaptcha();
    }
  }, []);

  const form = useForm<PhoneFormValues>({ mode: 'onSubmit', resolver: yupResolver(phoneSchema) });
  const {
    handleSubmit,
    setError,
    clearErrors,
    formState: { isSubmitting, errors },
  } = form;

  const showCaptchaWidget = isCaptchaRequired && Boolean(hcaptchaSiteKey);

  const onSubmit = async (data: PhoneFormValues) => {
    const phoneNumber = dialingCode + data.phone;

    if (showCaptchaWidget && !captchaTokenRef.current) {
      setError('phone', {
        type: 'manual',
        message: formatMessage({
          id: 'loginModal.completeCaptchaFirst',
          defaultMessage: 'Please complete the verification above before continuing.',
        }),
      });
      return;
    }

    try {
      const payload: AuthSignupRequest = { phone: phoneNumber };
      if (captchaTokenRef.current) payload.captchaToken = captchaTokenRef.current;

      const response = await apiAuthSignup(payload);
      const session = extractSignupSession(response);
      if (!session) {
        setError('phone', {
          type: 'manual',
          message: formatMessage({
            id: 'loginModal.signupUnexpectedResponse',
            defaultMessage: 'Something went wrong. Please try again.',
          }),
        });
        return;
      }
      if (parseSignupCaptchaRequirement(response).required) writeSignupCaptchaPersisted();
      captchaTokenRef.current = null;
      hcaptchaRef.current?.resetCaptcha();
      onCredential({ phone: phoneNumber, session });
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 403) {
        if (!hcaptchaSiteKey) {
          setError('phone', {
            type: 'manual',
            message: formatMessage({
              id: 'loginModal.captchaConfigError',
              defaultMessage: 'Verification could not be loaded. Please try again later.',
            }),
          });
          return;
        }
        captchaTokenRef.current = null;
        hcaptchaRef.current?.resetCaptcha();
        setIsCaptchaRequired(true);
        writeSignupCaptchaPersisted();
        setError('phone', {
          type: 'manual',
          message: formatMessage({
            id: 'loginModal.captchaRequiredFallback',
            defaultMessage: 'Please complete the verification below.',
          }),
        });
        return;
      }
      if (axios.isAxiosError(e) && e.response?.status === 400) {
        setError('phone', {
          type: 'manual',
          message:
            typeof e.response.data?.message === 'string'
              ? e.response.data.message
              : formatMessage({
                  id: 'loginModal.signupUnexpectedResponse',
                  defaultMessage: 'Something went wrong. Please try again.',
                }),
        });
      }
    }
  };

  return {
    dialingCode,
    form,
    errors,
    isSubmitting,
    showCaptchaWidget,
    hcaptchaRef,
    hcaptchaSiteKey,
    handleSubmit: handleSubmit(onSubmit),
    onCaptchaVerify: (token: string) => {
      captchaTokenRef.current = token;
      clearErrors('phone');
    },
    onCaptchaExpire: () => {
      captchaTokenRef.current = null;
      hcaptchaRef.current?.resetCaptcha();
    },
    onCaptchaError: () =>
      setError('phone', {
        type: 'manual',
        message: formatMessage({
          id: 'loginModal.captchaLoadError',
          defaultMessage: 'Verification could not be shown. Please try again.',
        }),
      }),
  };
};
